import { useContext, useEffect, useRef } from 'react'
import { RemoteDesktopControlEventTypes } from '../pages/CollaboratePage/Types'
import * as Constants from './Constants'
import { logger } from './Logger'
import { winKeyHook } from './IpcWinKeyHook'
import { env } from './EnvironmentVariables'
import { AppContext, AppContextType } from '../AppContext'
import { detectBrowerOs } from '../utils'

const log = logger.createChildLogger({ name: 'ScreenControlInputController' })

const browserOs = detectBrowerOs()

// NOTE(WinSpecialSilentCombo)
// If (and only if) the key combos WIN + x or WIN + ALT + x are pressed on Windows,
// where 'x' is one of {'w', 'r', 't', 'g', 'b', 'm'}, then the 'keydown' event for the
// character key is not fired.
// Notice the 'if and only if'. The keydown event _is_ fired for WIN + CTRL + ALT + g,
// while is NOT for WIN + g or WIN + ALT + g.
const winSpecialSilentComboKeys = ['w', 'r', 't', 'g', 'b', 'm']

class ModifierKeysState {
    s: boolean = false
    m: boolean = false
    a: boolean = false
    c: boolean = false

    isMetaKeySetByWinHook: boolean = false

    constructor(props?: { s: boolean; m: boolean; a: boolean; c: boolean; isMetaKeySetByWinHook: boolean }) {
        if (props) {
            this.s = props.s
            this.m = props.m
            this.a = props.a
            this.c = props.c
            this.isMetaKeySetByWinHook = props.isMetaKeySetByWinHook
        }
    }

    // see NOTE(WinSpecialSilentCombo)
    isInWinSpecialSilentComboState(): boolean {
        if (!this.isMetaKeySetByWinHook) {
            return false
        }
        return (!this.s && this.m && !this.a && !this.c) || (!this.s && this.m && this.a && !this.c)
    }
}

export default function ScreenControlInputController({
    srcObject,
    videoContainerEl,
    onRemoteDesktopControlEvent,
    onVideoAreaClick,
}: {
    srcObject: MediaStream
    videoContainerEl: HTMLElement | null
    onRemoteDesktopControlEvent?: (eventType: RemoteDesktopControlEventTypes, data: any) => void
    onVideoAreaClick?: () => void
}) {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const lastModKeysStateRef = useRef<ModifierKeysState>(new ModifierKeysState())

    useEffect(
        function winKeyHookHandler() {
            if (env.OS !== 'win32') {
                return
            }

            const onkeyHandler = (data: any) => {
                if (!onRemoteDesktopControlEvent) {
                    return
                }

                let pressDirection: 'keydown' | 'keyup' = 'keyup'
                let key: string | null = null
                if (data.keydown) {
                    pressDirection = 'keydown'
                    key = data.keydown
                } else if (data.keyup) {
                    pressDirection = 'keyup'
                    key = data.keyup
                }

                const lastModKeysState = lastModKeysStateRef.current

                log.isVerbose() &&
                    log.verbose('winKeyHookHandler', 'hooked-key event.', { pressDirection, key, lastModKeysState })

                if (key === 'win') {
                    if (lastModKeysState.m && pressDirection === 'keydown') {
                        log.silly('winKeyHookHandler', `prevented duplicate WIN keydown.`)
                    } else {
                        lastModKeysState.m = pressDirection === 'keydown'
                        lastModKeysState.isMetaKeySetByWinHook = true

                        onRemoteDesktopControlEvent(pressDirection, {
                            code: 'metaleft',
                            shiftKey: lastModKeysState.s,
                            metaKey: lastModKeysState.m,
                            altKey: lastModKeysState.a,
                            ctrlKey: lastModKeysState.c,
                        })
                    }
                } else if (key === 'tab') {
                    onRemoteDesktopControlEvent(pressDirection, {
                        code: 'tab',
                        shiftKey: lastModKeysState.s,
                        metaKey: lastModKeysState.m,
                        altKey: lastModKeysState.a,
                        ctrlKey: lastModKeysState.c,
                    })
                } else if (key && winSpecialSilentComboKeys.includes(key)) {
                    if (pressDirection === 'keydown' && lastModKeysState.isInWinSpecialSilentComboState()) {
                        const code = 'Key' + key.toLocaleUpperCase('en')
                        log.silly('winKeyHookHandler', `emitting 'keydown' for windows special silent code '${code}'.`)
                        onRemoteDesktopControlEvent('keydown', {
                            code: code,
                            repeat: false,
                            mod: lastModKeysStateRef.current,
                        })
                    }
                }
            }

            log.verbose('winKeyHookHandler', `winKeyHook.addListener() for event 'onkey'.`)
            winKeyHook.addListener('onkey', onkeyHandler)

            return () => {
                log.verbose('winKeyHookHandler', `winKeyHook.removeListener() for event 'onkey'.`)
                winKeyHook.removeListener('onkey', onkeyHandler)
            }
        },
        [onRemoteDesktopControlEvent]
    )

    useEffect(() => {
        if (videoContainerEl == null) {
            return
        }

        let handlers: any = {}

        const videoEl = videoContainerEl.querySelector('video')
        if (!videoEl) {
            return
        }

        let videoSettings: MediaTrackSettings | null = null
        const setVideoSettings = () => {
            if (!videoSettings?.width || !videoSettings?.height) {
                srcObject?.getVideoTracks().forEach((t) => {
                    videoSettings = t.getSettings()
                })
            }
        }

        const getCurrentModifierState = (ev: MouseEvent | KeyboardEvent): ModifierKeysState => {
            // `ev.getModifierState('Meta')` always returns `false` on Windows.
            const state = new ModifierKeysState({
                a: ev.getModifierState('Alt') || ev.altKey,
                c: ev.getModifierState('Control') || ev.ctrlKey,
                m: ev.getModifierState('Meta') || ev.metaKey,
                s: ev.getModifierState('Shift') || ev.shiftKey,
                isMetaKeySetByWinHook: lastModKeysStateRef.current.isMetaKeySetByWinHook,
            })

            if (lastModKeysStateRef.current.isMetaKeySetByWinHook) {
                state.m = lastModKeysStateRef.current.m
            }

            log.isSilly() && log.silly('getCurrentModifierState', `returning.`, { state })

            lastModKeysStateRef.current = state

            return state
        }

        if (onRemoteDesktopControlEvent) {
            let timeout: NodeJS.Timeout | null = null
            handlers.mousemoveHandler = (ev: MouseEvent) => {
                if (timeout == null) {
                    timeout = setTimeout(() => {
                        timeout = null
                    }, Constants.screenControlDebounceMs)

                    setVideoSettings()
                    const res = convertLocalCoordToPercentageCoord(ev, videoEl, videoSettings)
                    if (res && onRemoteDesktopControlEvent) {
                        const { x, y, xp, yp } = res
                        onRemoteDesktopControlEvent('mousemove', { x, y, xp, yp })
                    }
                }
            }
            handlers.mousedownHandler = (ev: MouseEvent) => {
                setVideoSettings()

                const mod = getCurrentModifierState(ev)

                const res = convertLocalCoordToPercentageCoord(ev, videoEl, videoSettings)
                if (res && onRemoteDesktopControlEvent) {
                    onRemoteDesktopControlEvent('mousedown', {
                        x: res.x,
                        y: res.y,
                        xp: res.xp,
                        yp: res.yp,
                        b: ev.button,
                        mod,
                    })
                }
            }
            handlers.mouseupHandler = (ev: MouseEvent) => {
                setVideoSettings()

                const mod = getCurrentModifierState(ev)

                const res = convertLocalCoordToPercentageCoord(ev, videoEl, videoSettings)
                if (res && onRemoteDesktopControlEvent) {
                    onRemoteDesktopControlEvent('mouseup', {
                        x: res.x,
                        y: res.y,
                        xp: res.xp,
                        yp: res.yp,
                        b: ev.button,
                        mod,
                    })
                }
            }
            handlers.keydownHandler = (ev: KeyboardEvent) => {
                ev.preventDefault()

                const mod = getCurrentModifierState(ev)

                setVideoSettings()
                const { keyCode, code, repeat, isComposing } = ev

                // is composing (IME)
                if (isComposing || keyCode === 229) {
                    log.isSilly() && log.silly('keydownHandler', 'is composing key; no-op.', { code, repeat, mod })
                    return
                }

                if (!env.IS_ELECTRON && browserOs === 'win32' && (code === 'MetaLeft' || code === 'MetaRight')) {
                    appDispatch({
                        type: 'new_message_dialog',
                        value: {
                            id: 'browser_screen_control_win_key_not_supported_warning',
                            text:
                                'Shortcuts using the Windows key is not supported on the browser because ' +
                                'the browser does not detect them.\nPlease download the app to use this feature.',
                            negativeButtonDoNotShowAgain: true,
                        },
                    })
                }

                log.isSilly() && log.silly('keydownHandler', '', { code, repeat, mod })

                onRemoteDesktopControlEvent && onRemoteDesktopControlEvent('keydown', { code, repeat, mod })
            }
            handlers.keyupHandler = (ev: KeyboardEvent) => {
                if (env.TEST_SCREEN_CONTROL_STUCK_KEY_AT_SENDER) {
                    return
                }

                ev.preventDefault()

                const mod = getCurrentModifierState(ev)

                setVideoSettings()

                const { code } = ev

                log.isSilly() && log.silly('keyupHandler', '', { code, mod })

                if (env.TEST_SCREEN_CONTROL_STUCK_KEY_AT_RECEIVER) {
                    return
                }
                onRemoteDesktopControlEvent && onRemoteDesktopControlEvent('keyup', { code, mod })
            }
            handlers.wheelHandler = (ev: WheelEvent) => {
                setVideoSettings()
                let { deltaX, deltaY } = ev

                const factor = appState.appSettings.screenControlWheelScaleFactor || 1.0

                deltaX *= factor
                deltaY *= factor
                onRemoteDesktopControlEvent && onRemoteDesktopControlEvent('wheel', { deltaX, deltaY })
            }
            handlers.clickHandler = (ev: MouseEvent) => {
                // NOTE: clicks handled by mouseup/mousedown events.
                //const { button } = ev
                //onRemoteDesktopControlEvent && onRemoteDesktopControlEvent('click', { button, double: false })
            }

            videoEl.addEventListener('mousemove', handlers.mousemoveHandler)
            videoEl.addEventListener('mousedown', handlers.mousedownHandler)
            videoEl.addEventListener('mouseup', handlers.mouseupHandler)
            videoEl.addEventListener('keydown', handlers.keydownHandler)
            videoEl.addEventListener('keyup', handlers.keyupHandler)
            videoEl.addEventListener('wheel', handlers.wheelHandler)
            videoEl.addEventListener('click', handlers.clickHandler)
            videoEl.addEventListener('dblclick', handlers.dblclickHandler)
            videoEl.addEventListener('contextmenu', handlers.contextmenuHandler)
        } else if (onVideoAreaClick) {
            handlers.clickHandler = (ev: MouseEvent) => {
                // NOTE: clicks handled by mouseup/mousedown events.
                setVideoSettings()
                const res = convertLocalCoordToPercentageCoord(ev, videoEl, videoSettings)
                if (res) {
                    res.x /= videoSettings?.width || 1
                    res.y /= videoSettings?.height || 1
                    if (res.x >= 0 && res.x <= 1 && res.y >= 0 && res.y <= 1) {
                        onVideoAreaClick()
                    }
                }
            }
            videoEl.addEventListener('click', handlers.clickHandler)
        }

        return () => {
            if (handlers && videoEl) {
                handlers.mousemoveHandler && videoEl.removeEventListener('mousemove', handlers.mousemoveHandler)
                handlers.mousedownHandler && videoEl.removeEventListener('mousedown', handlers.mousedownHandler)
                handlers.mouseupHandler && videoEl.removeEventListener('mouseup', handlers.mouseupHandler)
                handlers.keydownHandler && videoEl.removeEventListener('keydown', handlers.keydownHandler)
                handlers.keyupHandler && videoEl.removeEventListener('keyup', handlers.keyupHandler)
                handlers.wheelHandler && videoEl.removeEventListener('wheel', handlers.wheelHandler)
                handlers.clickHandler && videoEl.removeEventListener('click', handlers.clickHandler)
                handlers.dblclickHandler && videoEl.removeEventListener('dblclick', handlers.dblclickHandler)
                handlers.contextmenuHandler && videoEl.removeEventListener('contextmenu', handlers.contextmenuHandler)
            }
        }
    }, [
        onRemoteDesktopControlEvent,
        onVideoAreaClick,
        srcObject,
        videoContainerEl,
        appState.appSettings.screenControlWheelScaleFactor,
        appDispatch,
    ])

    return null
}

function convertLocalCoordToPercentageCoord(
    ev: MouseEvent,
    videoElement: HTMLVideoElement | null,
    videoInfo: MediaTrackSettings | null
) {
    const { pageX, pageY, target } = ev
    if (!target) {
        console.error(`!target. No-op.`)
        return
    }
    if (videoElement == null) {
        console.error(`videoElement is null! No-op.`)
        return
    }

    // get absolute position of the videoElement relative to the document origin
    // in order to offset the position properly.
    const offset = videoElement.getBoundingClientRect()

    // w/h of the video track
    const videoDim = {
        width: videoInfo?.width, // c
        height: videoInfo?.height, // d
    }
    if (!videoDim.width || !videoDim.height) {
        console.error(`!videoDim.width || !videoDim.height. No-op.`)
        return null
    }

    // w/h of the video element.
    const elementDim = {
        width: videoElement.getBoundingClientRect().width, // a
        height: videoElement.getBoundingClientRect().height, // b
    }

    // Let bb be the elementDim.height if the aspect ratio of videoDim was respected.
    const bb = (elementDim.width * videoDim.height) / videoDim.width
    const yOffset = (elementDim.height - bb) / 2

    // let cc be the elementDim.width if the aspect ratio of videoDim was respected.
    const cc = (elementDim.height * videoDim.width) / videoDim.height
    const xOffset = (elementDim.width - cc) / 2

    // position of the click with origin on the video of the video element.
    if (yOffset >= 0) {
        var clickPosition = {
            x: pageX - offset.left,
            y: pageY - offset.top - yOffset,
        }
    } else {
        var clickPosition = {
            x: pageX - offset.left - xOffset,
            y: pageY - offset.top,
        }
    }

    //     console.log(`***
    //     pageXY=<${pageX}, ${pageY}>
    //     videoDim=<${videoDim.width}, ${videoDim.height}>
    //     elementDim=<${elementDim.width}, ${elementDim.height}>
    //     offset=${JSON.stringify(offset)}
    //     bb=${bb}
    //     yOffset=${JSON.stringify(yOffset)}
    //     clickPosition=<${clickPosition.x}, ${clickPosition.y}>
    // `)

    if (yOffset >= 0) {
        var originalVideoClickPosition = {
            x: clickPosition.x * (videoDim.width / elementDim.width),
            y: clickPosition.y * (videoDim.height / bb),

            // xp and yp introduced in v1.4.4.
            xp: clickPosition.x / elementDim.width,
            yp: clickPosition.y / bb,
        }
    } else {
        var originalVideoClickPosition = {
            x: clickPosition.x * (videoDim.width / cc),
            y: clickPosition.y * (videoDim.height / elementDim.height),

            // xp and yp introduced in v1.4.4.
            xp: clickPosition.x / cc,
            yp: clickPosition.y / elementDim.height,
        }
    }

    return originalVideoClickPosition
}
